/**
 * Modal — единый примитив модалки.
 *
 * Используется вместо 5+ self-rolled модалок в проекте:
 *   libraries/SaveLibraryDialog, libraries/LibraryBar (delete confirm),
 *   admin/ApiKeysConfig (delete confirm), files/DataPreviewModal,
 *   generation/DetailViewModal, опционально admin/MasterTemplatesConfig drawer.
 *
 * Гарантии:
 * - Backdrop:        fixed inset-0 z-50 bg-black/60 backdrop-blur-sm
 * - Outside-click:   pointerdown на backdrop закрывает
 * - Esc:             keydown Escape закрывает
 * - Body lock:       document.body.style.overflow = 'hidden' пока open
 * - A11y:            aria-modal=true, role=dialog, aria-labelledby на title
 *
 * Использование:
 *   <Modal open={isOpen} onClose={() => setIsOpen(false)} title="Heading" footer={...}>
 *     <p>Body</p>
 *   </Modal>
 *
 * Слоты:
 *   - title: ReactNode (рендерится как <h2> с auto id для aria-labelledby).
 *   - children: основное содержимое.
 *   - footer?: ReactNode (опционально, sticky bottom в карточке).
 *   - maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' (default 'md').
 *   - onClose: обязателен — даже backdrop закрывает.
 */
import { type ReactNode, useEffect, useId, useRef } from 'react';

import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

type ModalMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const MAX_WIDTH: Record<ModalMaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: ModalMaxWidth;
  /**
   * Phase 3 (Files UX Refactor 2026-08-08): позволяет override'нуть
   * дефолтные max-w-* классы. Используется DataPreviewModal для ширины
   * ~70vw (`max-w-[min(70vw,1100px)]`) и DataPreviewModal-internal layout
   * (`max-h-[85vh] flex flex-col`). Мержится поверх дефолта, поэтому
   * бэкграунд/shadow/width базы не теряются.
   */
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'md',
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  // Esc + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Outside-click handler — fires only when pointerdown target === backdrop itself
  const onBackdropPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onPointerDown={onBackdropPointerDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cn(
          'card w-full shadow-2xl shadow-black/40',
          MAX_WIDTH[maxWidth],
          className,
        )}
      >
        {title && (
          <div className="flex items-center justify-between gap-4 mb-3">
            <h2 id={titleId} className="text-base font-semibold text-surface-100">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-surface-500 hover:text-surface-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div>{children}</div>
        {footer && (
          <div className="mt-4 flex items-center justify-end gap-2">{footer}</div>
        )}
      </div>
    </div>
  );
}
